import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ApiError,
  type AppearanceFieldErrors,
  type WhiteLabel,
  type WidgetAppearanceExtras,
  assistantApi,
} from "./api.ts";
import { resetSharedAssistantConfig, sharedAssistantConfig } from "./appearance-context.tsx";
import { mergeOverDefaults } from "./savedConfig.mjs";

// Mirrors tools/appearance_studio_config.py's two DEFAULT_* dicts exactly --
// same field names, same values. Frozen literal so the studio has something
// correct to render before the first GET resolves, same as aria-context.tsx.
const DEFAULT_EXTRAS: WidgetAppearanceExtras = Object.freeze({
  version: 1,
  theme: Object.freeze({ preset: "custom", scheme: "auto", density: "standard" }),
  gradient: Object.freeze({ header_enabled: false, angle: 120, panel_fill: "solid" }),
  launcher_extra: Object.freeze({
    label_mode: "hover", shadow: "high", animation: "pulse",
    badge_enabled: true, badge_count: "1",
    teaser_enabled: true, teaser_text: "Need a hand? I'm right here.",
    teaser_delay_s: 6, mobile_hide: false,
  }),
  panel_extra: Object.freeze({
    width: 376, height: 520, radius: 18, border_width: 1,
    elevation: "high", blur: 18, glass: true, mobile_behavior: "full",
  }),
  header: Object.freeze({
    layout: "compact", avatar_size: 34, avatar_radius_pct: 50,
    status_dot: true, subtitle_visible: true,
    subtitle_text: "Typically replies in minutes",
    actions: Object.freeze(["minimize", "close"]) as unknown as string[],
  }),
  messages: Object.freeze({
    bubble_style: "solid", radius: 14, tail: true,
    avatar_per_message: false, stamps: true, agent_label: false,
    cites: true, typing_style: "dots", max_width_pct: 84, gap: 9,
  }),
  composer: Object.freeze({
    // Empty keeps the widget's own per-language placeholder -- see api.ts.
    placeholder: "",
    send_style: "label", attach: true, emoji: true, voice: false,
    quick_reply_style: "pill", footer: true,
    footer_text: "AI-assisted · a human is one tap away",
    background: "rgba(255,255,255,.03)", badge_color: "#FF7A8A",
  }),
  typography: Object.freeze({
    font: "geist", size: 14, title_size: 14, title_weight: 650,
    line_height: 1.5, letter_spacing: 0,
  }),
  motion: Object.freeze({
    open_style: "scale", duration_ms: 320, easing: "spring",
    message_entrance: "rise", launcher_hover: "lift", reduce_motion: true,
  }),
  content: Object.freeze({
    away_message: "We're offline right now — leave a note and we'll reply by 9am.",
    csat_prompt: "How did that go?",
    escalate_line: "Bringing in a teammate now — one moment.",
    language: "en", rtl: false,
  }),
  accessibility: Object.freeze({
    focus_style: "ring", focus_color: "#3FCF8E", focus_width: 3,
    min_font_size: 14, shortcut: "alt+/",
    launcher_aria_label: "Open support chat",
    announce_new_messages: true, high_contrast_toggle: false,
  }),
  advanced: Object.freeze({ custom_css: "" }),
}) as unknown as WidgetAppearanceExtras;

const DEFAULT_WHITE_LABEL: WhiteLabel = Object.freeze({
  version: 1,
  apply: "mark",
  replace_product_name: false,
  workspace_label: "",
  use_logo_as_mark: true,
  use_logo_as_favicon: false,
  rename_browser_tab: false,
  show_powered_by: true,
});

export function defaultExtras(): WidgetAppearanceExtras {
  // Deep-ish clone: every nested object below is re-spread so a caller
  // mutating a draft never reaches back into the frozen literal above.
  return {
    version: 1,
    theme: { ...DEFAULT_EXTRAS.theme },
    gradient: { ...DEFAULT_EXTRAS.gradient },
    launcher_extra: { ...DEFAULT_EXTRAS.launcher_extra },
    panel_extra: { ...DEFAULT_EXTRAS.panel_extra },
    header: { ...DEFAULT_EXTRAS.header, actions: [...DEFAULT_EXTRAS.header.actions] },
    messages: { ...DEFAULT_EXTRAS.messages },
    composer: { ...DEFAULT_EXTRAS.composer },
    typography: { ...DEFAULT_EXTRAS.typography },
    motion: { ...DEFAULT_EXTRAS.motion },
    content: { ...DEFAULT_EXTRAS.content },
    accessibility: { ...DEFAULT_EXTRAS.accessibility },
    advanced: { ...DEFAULT_EXTRAS.advanced },
  };
}

export function defaultWhiteLabel(): WhiteLabel {
  return { ...DEFAULT_WHITE_LABEL };
}

export type AppearanceStudioStatus = "loading" | "ready" | "saving" | "saved" | "error";

interface AppearanceStudioContextValue {
  widgetAppearanceExtras: WidgetAppearanceExtras;
  whiteLabel: WhiteLabel;
  status: AppearanceStudioStatus;
  fieldErrors: AppearanceFieldErrors;
  publishedVersion: number;
  save: (next: { widgetAppearanceExtras: WidgetAppearanceExtras; whiteLabel: WhiteLabel }) =>
    Promise<{ widgetAppearanceExtras: WidgetAppearanceExtras; whiteLabel: WhiteLabel }>;
  reload: () => Promise<void>;
}

const AppearanceStudioContext = createContext<AppearanceStudioContextValue | null>(null);

function normalizedExtras(
  response: Awaited<ReturnType<typeof assistantApi.get>>,
): { widgetAppearanceExtras: WidgetAppearanceExtras; whiteLabel: WhiteLabel } {
  const config = response.result?.config;
  if (
    !response.ok || !config
    || config.widget_appearance_extras?.version !== 1
    || config.white_label?.version !== 1
  ) {
    throw new Error(response.error || "Appearance Studio settings were unavailable.");
  }
  // Merged over the defaults for the same reason as widget_appearance: a
  // server that predates `composer.placeholder` sends no placeholder, and the
  // stage calls .trim() on it.
  return {
    widgetAppearanceExtras: mergeOverDefaults(defaultExtras(), config.widget_appearance_extras),
    whiteLabel: mergeOverDefaults(defaultWhiteLabel(), config.white_label),
  };
}

function apiFieldErrors(error: unknown): AppearanceFieldErrors {
  if (!(error instanceof ApiError) || typeof error.body !== "object" || error.body === null) {
    return {};
  }
  const body = error.body as Record<string, unknown>;
  const raw = body.field_errors;
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return {};

  return Object.fromEntries(
    Object.entries(raw).filter(
      (entry): entry is [string, string] => typeof entry[1] === "string",
    ),
  );
}

export function AppearanceStudioProvider({ children }: { children: ReactNode }) {
  const [widgetAppearanceExtras, setWidgetAppearanceExtras] = useState(defaultExtras);
  const [whiteLabel, setWhiteLabel] = useState(defaultWhiteLabel);
  const [status, setStatus] = useState<AppearanceStudioStatus>("loading");
  const [fieldErrors, setFieldErrors] = useState<AppearanceFieldErrors>({});
  const [publishedVersion, setPublishedVersion] = useState(0);
  const generation = useRef(0);
  const controller = useRef<AbortController | null>(null);

  const reload = useCallback(async () => {
    controller.current?.abort();
    const requestController = new AbortController();
    controller.current = requestController;
    const requestGeneration = ++generation.current;
    setStatus("loading");
    setFieldErrors({});

    try {
      const next = normalizedExtras(await sharedAssistantConfig());
      if (requestController.signal.aborted || generation.current !== requestGeneration) return;
      setWidgetAppearanceExtras(next.widgetAppearanceExtras);
      setWhiteLabel(next.whiteLabel);
      setStatus("ready");
    } catch {
      if (requestController.signal.aborted || generation.current !== requestGeneration) return;
      // Defaults or the last known-good state remain intact on every failure.
      setStatus("error");
    }
  }, []);

  useEffect(() => {
    void reload();
    /* No cleanup. The read is shared (`sharedAssistantConfig`), so aborting it
       on unmount would cancel a request the OTHER provider is still waiting on
       -- and under StrictMode the cleanup would abort the very request the
       remount then has to issue again, which is the double GET this change
       removes. A response that lands after unmount is dropped by the
       `generation` guard inside `reload`, and setting state on an unmounted
       component is a no-op in React 18+. `save()` keeps its own controller:
       that request has exactly one owner. */
  }, [reload]);

  // Publish only broadcasts the new studio state into this context on a
  // successful save -- never on every keystroke a draft editor makes. This
  // is the `white_label` propagation contract: an unpublished draft must
  // never repaint the live white-labelled dashboard chrome before it clears
  // the save gate. Mirrors writeTenant()'s documented behaviour in the
  // design handoff and aria-context.tsx's identical rule for persona.
  const save = useCallback(async (next: { widgetAppearanceExtras: WidgetAppearanceExtras; whiteLabel: WhiteLabel }) => {
    controller.current?.abort();
    const requestController = new AbortController();
    controller.current = requestController;
    const requestGeneration = ++generation.current;
    setStatus("saving");
    setFieldErrors({});

    try {
      const written = await assistantApi.update(
        { widget_appearance_extras: next.widgetAppearanceExtras, white_label: next.whiteLabel },
        requestController.signal,
      );
      // WRITE BARRIER -- see resetSharedAssistantConfig's header. The studio and
      // the appearance provider read the same row, so a save here is a stale
      // read for the other one too.
      resetSharedAssistantConfig();
      const saved = normalizedExtras(written);
      if (requestController.signal.aborted || generation.current !== requestGeneration) {
        return saved;
      }
      setWidgetAppearanceExtras(saved.widgetAppearanceExtras);
      setWhiteLabel(saved.whiteLabel);
      setPublishedVersion((v) => v + 1);
      setStatus("saved");
      return saved;
    } catch (error) {
      if (!requestController.signal.aborted && generation.current === requestGeneration) {
        setFieldErrors(apiFieldErrors(error));
        setStatus("error");
      }
      throw error;
    }
  }, []);

  const value = useMemo<AppearanceStudioContextValue>(() => ({
    widgetAppearanceExtras,
    whiteLabel,
    status,
    fieldErrors,
    publishedVersion,
    save,
    reload,
  }), [widgetAppearanceExtras, whiteLabel, status, fieldErrors, publishedVersion, save, reload]);

  return (
    <AppearanceStudioContext.Provider value={value}>
      {children}
    </AppearanceStudioContext.Provider>
  );
}

export function useAppearanceStudio(): AppearanceStudioContextValue {
  const value = useContext(AppearanceStudioContext);
  if (!value) {
    throw new Error("useAppearanceStudio must be used inside <AppearanceStudioProvider>");
  }
  return value;
}
